import { useMutation } from '@tanstack/react-query';
import type {
  ChangePasswordInput,
  ForgotPasswordInput,
  ForgotPasswordResult,
  LoginInput,
  LogoutResult,
  MeResult,
  RefreshResult,
  RegisterInput,
  ResendVerificationResult,
  ResetPasswordInput,
  SessionResult,
  UpdateProfileInput,
  VerifyEmailInput,
  VerifyEmailResult,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

/** POST /api/v1/auth/register */
export function register(input: RegisterInput): Promise<SessionResult> {
  // The new workspace works in the time zone of the browser it was created from.
  const timeZone = input.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return apiClient.post<SessionResult>('/auth/register', { ...input, timeZone });
}

/** POST /api/v1/auth/login */
export function login(input: LoginInput): Promise<SessionResult> {
  return apiClient.post<SessionResult>('/auth/login', input);
}

/** POST /api/v1/auth/refresh - the refresh token travels in the httpOnly cookie. */
export function refresh(): Promise<RefreshResult> {
  return apiClient.post<RefreshResult>('/auth/refresh', {});
}

/** POST /api/v1/auth/logout - the access token is injected by the client. */
export function logout(allSessions = false): Promise<LogoutResult> {
  return apiClient.post<LogoutResult>('/auth/logout', { allSessions });
}

/** GET /api/v1/auth/me */
export function getMe(): Promise<MeResult> {
  return apiClient.get<MeResult>('/auth/me');
}

export function useRegister() {
  return useMutation<SessionResult, ApiClientError, RegisterInput>({ mutationFn: register });
}

export function useLogin() {
  return useMutation<SessionResult, ApiClientError, LoginInput>({ mutationFn: login });
}

/** POST /api/v1/auth/forgot-password - always resolves the same way, whether or not the email exists. */
export function forgotPassword(input: ForgotPasswordInput): Promise<ForgotPasswordResult> {
  return apiClient.post<ForgotPasswordResult>('/auth/forgot-password', input);
}

/** POST /api/v1/auth/reset-password - the token comes from the emailed link. */
export function resetPassword(input: ResetPasswordInput): Promise<void> {
  return apiClient.post<void>('/auth/reset-password', input);
}

/** POST /api/v1/auth/verify-email - the token comes from the emailed link. */
export function verifyEmail(input: VerifyEmailInput): Promise<VerifyEmailResult> {
  return apiClient.post<VerifyEmailResult>('/auth/verify-email', input);
}

/** POST /api/v1/auth/resend-verification - the access token is injected by the client. */
export function resendVerification(): Promise<ResendVerificationResult> {
  return apiClient.post<ResendVerificationResult>('/auth/resend-verification');
}

export function useForgotPassword() {
  return useMutation<ForgotPasswordResult, ApiClientError, ForgotPasswordInput>({
    mutationFn: forgotPassword,
  });
}

export function useResetPassword() {
  return useMutation<void, ApiClientError, ResetPasswordInput>({ mutationFn: resetPassword });
}

export function useVerifyEmail() {
  return useMutation<VerifyEmailResult, ApiClientError, VerifyEmailInput>({
    mutationFn: verifyEmail,
  });
}

export function useResendVerification() {
  return useMutation<ResendVerificationResult, ApiClientError, void>({
    mutationFn: resendVerification,
  });
}

/** PATCH /api/v1/auth/me - updates displayName/phone; email is not editable here. */
export function updateProfile(input: UpdateProfileInput): Promise<MeResult> {
  return apiClient.patch<MeResult>('/auth/me', input);
}

/** POST /api/v1/auth/change-password - keeps the current session alive, revokes every other one. */
export function changePassword(input: ChangePasswordInput): Promise<void> {
  return apiClient.post<void>('/auth/change-password', input);
}

export function useUpdateProfile() {
  return useMutation<MeResult, ApiClientError, UpdateProfileInput>({ mutationFn: updateProfile });
}

export function useChangePassword() {
  return useMutation<void, ApiClientError, ChangePasswordInput>({ mutationFn: changePassword });
}
